import { useGlobalStore } from "@/store/GlobalStore";
import { useTranslation } from "react-i18next";
import {
  PictureGiftMemberRechargeCard,
  PictureGiftMemberWrapper,
  RechargePerMonth,
} from "./atom";
import { useUserPropertyStore } from "@/store2/userProperty/user-property-store";
import {
  OrderPaymentTypeCode,
  PaymentPurposeCode,
  PaymentTypeCode,
  QRCODE_QUERY_MAP,
  RechargeTypeCode,
  rechargeChannelsCode,
} from "project-config/constants";
import { useEffect, useState } from "react";
import { accSub } from "project-config/utils/acc";
import CheckIcon from "@/assets/icons/topup/check";
import VIPIcon from "@/assets/icons/topup/vip-icon";
import {
  CommonCardListWrapper,
  CommonCornerMark,
  CommonUpperPart,
} from "../../atom";
import { usePictureTopUpStore } from "../../store";
import { rulesContentList } from "@/components/top-up/help/activityUtil";
import { VipRechargeInfo } from "../../constants/picture-vip-info";
import { PaymentWay } from "../payment-way";
import { RechargeTips } from "../../help/tips";
import { BindActivityInfo, Detail } from "@/components/top-up/types";
import { ExtraWelfareTipType } from "../../types";
import ActivityRule from "@/components/top-up/components/activity-rule";
import { Checkbox } from "@renderbus/ui";

export function PictureGiftMember({
  activityInfo,
}: {
  activityInfo: BindActivityInfo;
}) {
  const { t } = useTranslation("topup");
  const { isZh, currencySymbol, userCumulative } = useGlobalStore();
  const amount = usePictureTopUpStore((state) => state.amount);
  const vipMemberType = usePictureTopUpStore((state) => state.vipMemberType);
  const paying = usePictureTopUpStore((state) => state.paying);
  const { setter, addMemberRecharge } = usePictureTopUpStore();
  const [openActivityRuleDrawer, setOpenActivityRuleDrawer] = useState(false);
  const [checkCard, setCheckCard] = useState<Detail>();
  const { userProperty } = useUserPropertyStore();
  const [extraTip, setExtraTip] = useState<ExtraWelfareTipType[]>();
  const [checkProtocol, setCheckProtocol] = useState(false);

  const amountConversion = (vipAmount: number) => {
    const content = VipRechargeInfo.find((item) => item.vipMonth === vipAmount);
    return content ? content.price : 0;
  };

  const rechargeAmount = (vipAmount: number) => {
    if (activityInfo?.rechargeType === RechargeTypeCode.SumRecharge) {
      const amountSum = accSub(
        amountConversion(vipAmount) || 0,
        userCumulative
      );
      return amountSum > 4 ? amountSum : 5;
    } else {
      return amountConversion(vipAmount);
    }
  };

  const pictureGiftMemberDetail = VipRechargeInfo.map((i) => {
    const detail = activityInfo.activityDetail.filter(
      (item) => item.vipAmount === i.vipMonth
    );
    if (detail.length) {
      return { ...detail[0], originalPrice: i.originalPrice };
    } else {
      return {
        amount: 0,
        vipAmount: i.vipMonth,
        vipPresentAmount: 0,
        presentAmount: 0,
        originalPrice: i.originalPrice,
      };
    }
  });

  const priceList = pictureGiftMemberDetail.map((item) =>
    rechargeAmount(item.vipAmount ?? 0)
  );

  const getQrCodeLink = () => {
    return `&${QRCODE_QUERY_MAP.orderType}=${
      OrderPaymentTypeCode.MemberOrder
    }&${QRCODE_QUERY_MAP.type}=${vipMemberType}&${QRCODE_QUERY_MAP.mount}=1&${
      QRCODE_QUERY_MAP.rechargeGoal
    }=${
      !checkCard?.vipPresentAmount
        ? PaymentPurposeCode.OrdinaryRecharge
        : PaymentPurposeCode.ActivityRecharge
    }`;
  };

  const onClickCantScanCode = async () => {
    const f = VipRechargeInfo.find(
      (item) => item.vipMonth === checkCard?.vipAmount
    );
    const params = {
      payMethod: PaymentTypeCode.Alipay,
      mount: 1,
      type: f?.type ?? 1,
      rechargeGoal: !checkCard?.vipPresentAmount
        ? PaymentPurposeCode.OrdinaryRecharge
        : PaymentPurposeCode.ActivityRecharge,
      rechargeChannels: rechargeChannelsCode.Web,
    };
    await addMemberRecharge(params);
  };

  useEffect(() => {
    const firstLevel = pictureGiftMemberDetail[0];
    // 设置默认选中的卡片
    setCheckCard(firstLevel);
    setter({
      amount: priceList[0],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const rechargeTips = new RechargeTips({
      amount: Number(amount),
      userProperty,
      currencySymbol,
      activityInfo,
      activityDetailItem: checkCard,
    });
    rechargeTips.init();
    setExtraTip(rechargeTips.extraWelfareTip);
  }, [amount, userProperty, currencySymbol, activityInfo, checkCard]);

  return (
    <PictureGiftMemberWrapper>
      <ActivityRule
        ruleList={rulesContentList(activityInfo, isZh)}
        openRuleDrawer={openActivityRuleDrawer}
        changeDrawer={(val) => {
          setOpenActivityRuleDrawer(val);
        }}
        activityInfo={activityInfo}
      />
      <CommonUpperPart>
        <CommonCardListWrapper>
          {pictureGiftMemberDetail.map((item: any, i) => {
            const price = priceList[i];
            // 效果图购买送会员中的默认会员（当该类活动只1~3个配置卡片时需要补充欠缺的默认会员卡片）
            const isGiftDefaultMember = !item.vipPresentAmount && !item.amount;
            const isGiftVip = !!item.vipPresentAmount;
            const isCheckedCard = amount === price;
            return (
              <PictureGiftMemberRechargeCard
                isActive={isCheckedCard}
                padding={"18px 12px 12px 14px"}
                gap={isGiftVip ? "0px" : "7px"}
                onClick={() => {
                  setCheckCard(item);
                  setter((state) => {
                    state.vipMemberType = VipRechargeInfo[i]?.type;
                    state.amount = price;
                  });
                }}
                key={item.id}
              >
                {item.iconWord && item.iconWord !== "" && (
                  <CommonCornerMark>{item.iconWord}</CommonCornerMark>
                )}
                <div className="recharge-vip">
                  {item.vipAmount}
                  {t("activity_member_free_member_month")}
                </div>
                <div className="recharge-price">
                  <div>
                    <span className="unit">{currencySymbol}</span>
                    <span className="price">{price}</span>
                    <span className="original-price">
                      {currencySymbol}
                      {item.originalPrice}
                    </span>
                  </div>
                  {isCheckedCard && <CheckIcon />}
                </div>
                {isGiftVip && (
                  <div className="extra-welfare">
                    <div className="tip-text">
                      {t("topUp_first_extra_welfare")}
                    </div>
                    <div className="gift-amount">
                      <VIPIcon />
                      <span>
                        {t("activity_member_free_member_vip")}
                        &nbsp;{item.vipAmount}
                        {t("activity_member_free_member_month")}
                      </span>
                    </div>
                  </div>
                )}
                {isGiftDefaultMember && item.vipAmount !== 1 && (
                  <RechargePerMonth>
                    {t("picture_vip_save", {
                      amount: Number(item.originalPrice - price),
                    })}
                  </RechargePerMonth>
                )}
              </PictureGiftMemberRechargeCard>
            );
          })}
        </CommonCardListWrapper>
      </CommonUpperPart>
      <PaymentWay
        disabledProtocol={!checkProtocol}
        paying={paying}
        onClickCantScanCode={onClickCantScanCode}
        qrcodeLink={getQrCodeLink()}
        extraTip={extraTip}
        extra={
          <Checkbox
            value={checkProtocol}
            onChange={(e) => setCheckProtocol(e.target.checked)}
            className="picture-vip-protocols"
          >
            <span>{t("picture_vip_protocol")}</span>
            <a
              href={
                "https://www.renderbus.com/support-new/xrxz/fwtkyts/xgthyxy/"
              }
              target={"_blank"}
              rel="noreferrer"
            >
              {t("picture_vip_protocol_link")}
            </a>
          </Checkbox>
        }
      />
    </PictureGiftMemberWrapper>
  );
}
